import {
  Box,
  Button,
  Flex,
  Skeleton,
  Stack,
  Text,
  TextInput,
  rem,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconDownload,
  IconFilter,
  IconSearch,
} from "@tabler/icons-react";
import { useMobileScreen } from "@/shared/hooks/useMobileScreen";

const ROW_COUNT = 8;

/**
 * Conteúdo dinâmico da listagem de contatos.
 *
 * Espelha `ContactsTable` (tabela desktop) e `ContactsMobileList` (cards no
 * mobile): mantém os cabeçalhos de coluna reais (conteúdo estático) e troca
 * apenas as linhas/valores dinâmicos por skeletons. Usado dentro da
 * `ContactsPage` enquanto a query da lista está pendente — por isso NÃO
 * renderiza o cabeçalho/busca (que a página já mantém vivos e interativos).
 */
export function ContactsContentSkeleton() {
  const { isMobileScreen } = useMobileScreen();

  // Mobile: espelha os cards de `ContactsMobileList`.
  if (isMobileScreen) {
    return (
      <Stack gap={0}>
        {Array.from({ length: ROW_COUNT }).map((_, index) => (
          <Flex
            key={index}
            align="center"
            gap={rem(10)}
            p={`${rem(12)} ${rem(16)}`}
            style={{
              borderBottom: "1px solid var(--mantine-color-default-border)",
            }}>
            <Skeleton
              circle
              height={rem(40)}
            />
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Skeleton
                height={rem(12)}
                width="55%"
                radius="xl"
              />
              <Skeleton
                height={rem(10)}
                width="35%"
                radius="xl"
                mt={rem(6)}
              />
              <Skeleton
                height={rem(16)}
                width={rem(72)}
                radius="xl"
                mt={rem(8)}
              />
            </Box>
            <Skeleton
              height={rem(10)}
              width={rem(28)}
              radius="xl"
            />
            <Skeleton
              circle
              height={rem(28)}
            />
          </Flex>
        ))}
      </Stack>
    );
  }

  // Desktop: espelha a tabela de `ContactsTable`.
  return (
    <Flex
      direction="column"
      gap={rem(12)}>
      <Box
        style={{
          border: "1px solid var(--mantine-color-default-border)",
          borderRadius: rem(12),
          overflow: "hidden",
        }}>
        {/* Cabeçalho real (conteúdo estático) — idêntico ao ContactsTable. */}
        <Flex
          align="center"
          gap={rem(12)}
          p={`${rem(10)} ${rem(16)}`}
          style={{
            background: "var(--mantine-color-background_1-0)",
            borderBottom: "1px solid var(--mantine-color-default-border)",
          }}>
          <Text
            fz={rem(12)}
            fw={700}
            c="dimmed"
            tt="uppercase"
            style={{ flex: 1 }}>
            Nome
          </Text>
          <Text
            fz={rem(12)}
            fw={700}
            c="dimmed"
            tt="uppercase"
            style={{ width: rem(140), flexShrink: 0 }}>
            Telefone
          </Text>
          <Text
            fz={rem(12)}
            fw={700}
            c="dimmed"
            tt="uppercase"
            style={{ flex: 1 }}>
            Tags
          </Text>
          <Text
            fz={rem(12)}
            fw={700}
            c="dimmed"
            tt="uppercase"
            style={{ width: rem(140), flexShrink: 0 }}>
            Último contato
          </Text>
          {/* Espaçador alinhado à coluna de ação. */}
          <Box style={{ width: rem(40), flexShrink: 0 }} />
        </Flex>

        {/* Linhas dinâmicas — skeleton. */}
        {Array.from({ length: ROW_COUNT }).map((_, index) => (
          <Flex
            key={index}
            align="center"
            gap={rem(12)}
            p={`${rem(10)} ${rem(16)}`}
            style={{
              borderBottom: "1px solid var(--mantine-color-default-border)",
            }}>
            <Flex
              gap={rem(10)}
              align="center"
              style={{ flex: 1, minWidth: 0 }}>
              <Skeleton
                circle
                height={rem(28)}
              />
              <Skeleton
                height={rem(12)}
                width="55%"
                radius="xl"
              />
            </Flex>
            <Box style={{ width: rem(140), flexShrink: 0 }}>
              <Skeleton
                height={rem(12)}
                width="80%"
                radius="xl"
              />
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Skeleton
                height={rem(20)}
                width={rem(72)}
                radius="xl"
              />
            </Box>
            <Box style={{ width: rem(140), flexShrink: 0 }}>
              <Skeleton
                height={rem(12)}
                width="60%"
                radius="xl"
              />
            </Box>
            <Box
              style={{
                width: rem(40),
                flexShrink: 0,
                display: "flex",
                justifyContent: "center",
              }}>
              <Skeleton
                circle
                height={rem(24)}
              />
            </Box>
          </Flex>
        ))}
      </Box>

      {/* Rodapé (contagem/paginação) — skeleton. */}
      <Flex
        align="center"
        justify="space-between"
        gap={rem(12)}>
        <Skeleton
          height={rem(12)}
          width={rem(160)}
          radius="xl"
        />
      </Flex>
    </Flex>
  );
}

/**
 * Esqueleto de página completo da tela de Contatos.
 *
 * Usado como fallback de Suspense do chunk lazy (antes de a `ContactsPage`
 * montar): renderiza TODO o chrome estático da página como conteúdo real —
 * título, legenda, campo de busca e botões de filtro/exportar — para que os
 * textos fixos apareçam de cara e a estrutura reflita a página carregada. As
 * regiões dinâmicas ficam a cargo do `ContactsContentSkeleton`.
 */
export function ContactsPageSkeleton() {
  const { isMobileScreen } = useMobileScreen();

  return (
    <Flex
      direction="column"
      mih="100vh"
      bg="background_3">
      <Box
        maw={rem(1200)}
        mx="auto"
        w="100%">
        {isMobileScreen && (
          <Flex
            align="center"
            gap={rem(8)}
            p={rem(16)}>
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconArrowLeft size={18} />}>
              Voltar
            </Button>
          </Flex>
        )}

        <Flex
          direction="column"
          p={isMobileScreen ? "16px" : "32px 48px"}
          gap={rem(20)}
          mih={isMobileScreen ? "calc(100vh - 60px)" : undefined}>
          {/* Cabeçalho (conteúdo estático). */}
          <Box>
            <Text
              component="h1"
              fw="bold"
              fz={rem(28)}
              ff="heading"
              c="plain_text">
              Contatos
            </Text>
            <Text
              fz={rem(14)}
              c="dimmed">
              Todos os clientes que já interagiram com sua conta. Use tags para
              organizar e segmentar.
            </Text>
          </Box>

          {/* Filtros (chrome estático) — busca e botões reais, ainda inertes. */}
          <Flex
            gap={rem(12)}
            align="center"
            wrap="wrap">
            <TextInput
              placeholder="Pesquisar por nome ou telefone..."
              leftSection={<IconSearch size={14} />}
              size="sm"
              radius="md"
              readOnly
              style={{ flex: 1, minWidth: rem(220) }}
            />
            <Button
              variant="default"
              size="sm"
              leftSection={<IconFilter size={14} />}>
              Filtrar por tag
            </Button>
            <Button
              variant="default"
              size="sm"
              leftSection={<IconDownload size={14} />}>
              Exportar CSV
            </Button>
          </Flex>

          {/* Conteúdo dinâmico. */}
          <ContactsContentSkeleton />
        </Flex>
      </Box>
    </Flex>
  );
}

export default ContactsPageSkeleton;
